import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { OrganizationsService } from '../organization/organizations.service';
import { RCOrganization } from '@rcenter/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthWorker } from './AuthWorker';
import { nextLastTokenFetchValue } from './lastTokenFetch';
import {
	PlatformsEnum,
	USERNAME_HEADER,
	REFRESH_TOKEN_HEADER,
	setTokens,
	NetworkClientBuilder,
} from 'auth';

@Injectable()
export class AuthenticationService {
	private tokenName = 'id_token';
	private renewTokenName = 'renew_token';

	currentOrganization: BehaviorSubject<RCOrganization> = new BehaviorSubject(null);
	userOrganizationsCount: BehaviorSubject<number> = new BehaviorSubject(0);

	/**
	 * Mirrors AuthWorker onto Angular subjects for remaining subscribers. AuthWorker is
	 * the only writer; this service does not write back. Subscribe unsubscribe functions
	 * are discarded because this is an app-lifetime singleton.
	 */
	constructor(
		private http: HttpClient,
		private authHttp: HttpClient,
		private organizationsService: OrganizationsService,
		private router: Router
	) {
		AuthWorker.subscribeToOrganization(organization => {
			if (this.currentOrganization.value !== organization) {
				this.currentOrganization.next(organization);
			}
		});

		AuthWorker.subscribeToUserOrganizationsCount(count => {
			if (this.userOrganizationsCount.value !== count) {
				this.userOrganizationsCount.next(count);
			}
		});
	}

	/**
	 * Performs a login request to the server
	 * @param email
	 * @param password
	 * @returns {Observable<RCOrganization>}
	 */
	login(email: string, password: string): Observable<RCOrganization> {
		const response = this.http.post<any>(`${environment.CS_URLS.API_DOMAIN}/login`, {
			email,
			password,
		});

		return new Observable<RCOrganization>(subscriber => {
			response.subscribe(
				(res: any) => {
					if (res.token) {
						this.setToken(res.token);
					}

					if (res.renewToken) {
						this.setRenewToken(res.renewToken);
					}

					this.fetchActiveOrganization().subscribe(
						o => {
							subscriber.next();
							subscriber.complete();
						},
						err => subscriber.error(err)
					);
				},
				err => subscriber.error(err)
			);
		});
	}

	/**
	 * @param {string} email
	 * @param {string} password
	 * @returns {Observable<any>}
	 */
	signIn(email: string, password: string) {
		return this.http
			.post<any>(environment.CS_URLS.API_DOMAIN + '/auth/login', {
				email,
				password,
				backoffice: true,
			})
			.pipe(
				map((response: any) => {
					const res = response;

					if (res.token) {
						this.setToken(res.token);
					}

					if (res.renewToken) {
						this.setRenewToken(res.renewToken);
					}

					return res;
				})
			);
	}

	renewToken(onSuccess?: () => void) {
		const tokens = AuthWorker.getTokens();

		const lastFetch = localStorage.getItem('lastTokenFetch');

		return this.authHttp
			.get(`${environment.CS_URLS.AUTH_ROOT}/refresh?platform=${PlatformsEnum.BO}`, {
				withCredentials: NetworkClientBuilder.includeCredentials,
				headers: new HttpHeaders({
					...(tokens.refreshToken && { [REFRESH_TOKEN_HEADER]: tokens.refreshToken }),
					...(tokens.username && { [USERNAME_HEADER]: tokens.username }),
				}),
			})
			.subscribe({
				next: tokens => {
					const now = new Date();
					const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
					const nextLastFetch = nextLastTokenFetchValue(lastFetch, now, timezone);

					if (nextLastFetch) {
						localStorage.setItem('lastTokenFetch', nextLastFetch);
					}

					setTokens(tokens);

					onSuccess?.();
				},
				error: error => {
					this.router.navigate(['/login']);
				},
			});
	}

	/**
	 * Performs a signup request to the server
	 * @returns {Observable<RCOrganization>}
	 */
	signup(data: {
		email: string;
		password: string;
		firstName: string;
		lastName: string;
		backoffice?: boolean;
	}): Observable<any> {
		data.backoffice = true;

		return this.http.post<any>(environment.CS_URLS.API_DOMAIN + '/auth/signup', data).pipe(
			map(res => res),
			map((res: any) => {
				if (res.token) {
					this.setToken(res.token);
				}

				if (res.renewToken) {
					this.setRenewToken(res.renewToken);
				}

				return res;
			})
		);
	}

	setToken(token: string) {
		localStorage.setItem(this.tokenName, token);
	}

	setRenewToken(token: string) {
		localStorage.setItem(this.renewTokenName, token);
	}

	getToken(): string {
		return localStorage.getItem(this.tokenName);
	}

	/**
	 * Resolves the user's active organization via APIv2 bootstrap endpoint, then hydrates
	 * full backoffice org details for currentOrganization consumers (timezone, currency, logo, etc.).
	 * Honors preferredOrganizationId / selected_org_id when authorized; otherwise first by name.
	 * Stores totalOrganizations for switcher visibility when the user has more than one.
	 */
	fetchActiveOrganization(preferredOrganizationId?: number): Observable<RCOrganization> {
		return this.organizationsService.fetchActiveOrganization(preferredOrganizationId);
	}

	/**
	 * Sends a password request to the server
	 * Email address must be provided in order to request a password reset
	 * @param email
	 * @returns {Observable<Response>}
	 */
	requestPasswordReset(email: string): Observable<any> {
		if (!email) throw new Error('Email was not provided for password reset service.');
		return this.http
			.get<any>(
				`${environment.CS_URLS.API_DOMAIN}/auth/password-reset?email=${encodeURIComponent(
					email
				)}&url=${encodeURIComponent(environment.SITE_URL)}`
			)
			.pipe(map(res => res));
	}

	/**
	 * Resets the user password using a token generated by the server
	 * @param newPassword
	 * @param passwordRepeat
	 * @param token
	 * @returns {Observable<Response>}
	 */
	resetPassword(newPassword: string, passwordRepeat: string, token: string): Observable<any> {
		if (!token) throw new Error('Token must be provided');
		if (newPassword !== passwordRepeat) throw new Error('Password must match');

		return this.http
			.post<any>(`${environment.CS_URLS.API_DOMAIN}/auth/password-reset`, {
				password: newPassword,
				passwordRepeat,
				token,
			})
			.pipe(map(res => res));
	}

	/**
	 * Logs the user out of the system
	 * It will remove the token from the localStorage
	 * and perform additional reset functionality
	 */
	logout(error?: string): void {
		AuthWorker.logout(error);
	}

	/**
	 * Checks if the user is authenticated.
	 * This method will also validate if the token is not expired
	 * @returns {boolean}
	 */
	isLoggedIn(): boolean {
		return AuthWorker.hasBondUserDetails();
	}

	/**
	 * Checks if the user is signed in and has submitted additional details.
	 *
	 * @returns {boolean} - Returns `true` if the user is logged in and has submitted additional information, otherwise `false`.
	 */
	isSignedInAndUserDetailsSubmitted(): boolean {
		// TODO: Update as backoffice signup flow decided
		return this.isLoggedIn();
	}

	/**
	 * Checks if the user has submitted additional information.
	 *
	 * This method uses the `CookiesWorker` to determine if the bond user details
	 * have been submitted.
	 *
	 * @returns {boolean} `true` if the user has submitted additional information, otherwise `false`.
	 */
	private hasSubmittedAdditionalInfo(): boolean {
		return AuthWorker.hasBondUserDetailsSubmitted();
	}

	getTokenData() {
		return AuthWorker.getTokenData();
	}
}
